import React, { useState, useEffect, useCallback } from 'react';
import api from '../api/axios';

const STATUS_COLORS = {
  pending:  { bg: 'rgba(212,175,55,0.1)',  border: 'rgba(212,175,55,0.3)',  text: '#E8B84B' },
  approved: { bg: 'rgba(34,197,94,0.08)', border: 'rgba(34,197,94,0.25)',  text: '#4ade80' },
  rejected: { bg: 'rgba(248,68,68,0.08)', border: 'rgba(248,68,68,0.25)',  text: '#f87171' },
};

const SERVICES_LABELS = {
  livestream:      '📡 Live Streaming',
  ceremonies:           '🪔 Online Ceremonies',
  sevaOffering:          '🎁 SevaOffering Delivery',
  donations:       '💛 Donations',
  virtual_darshan: '🕌 Virtual Darshan',
  festivals:       '🎉 Festival Streaming',
};

const CentreRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // Detail panel
  const [statusFilter, setStatusFilter] = useState('pending');
  const [toast, setToast] = useState(null);
  const [approveResult, setApproveResult] = useState(null); // Show temp password after approval
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(null);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/centre-requests?status=${statusFilter}`);
      setRequests(res.data.data || []);
    } catch (e) {
      showToast('error', 'Failed to load centre requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { fetchRequests(); }, [fetchRequests]);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 6000);
  };

  const handleApprove = async (id) => {
    setActionLoading(true);
    try {
      const res = await api.post(`/centre-requests/${id}/approve`, { adminNote: 'Approved by Super Admin' });
      setApproveResult(res.data.data);
      setRequests(prev => prev.filter(r => r._id !== id));
      setSelected(null);
      showToast('success', `Centre portal created successfully!`);
    } catch (e) {
      showToast('error', e.response?.data?.message || 'Failed to approve request.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (id) => {
    if (!rejectNote.trim()) { showToast('error', 'Please enter a rejection reason.'); return; }
    setActionLoading(true);
    try {
      await api.post(`/centre-requests/${id}/reject`, { adminNote: rejectNote });
      setRequests(prev => prev.filter(r => r._id !== id));
      setSelected(null);
      setShowRejectModal(null);
      setRejectNote('');
      showToast('success', 'Centre request rejected.');
    } catch (e) {
      showToast('error', e.response?.data?.message || 'Failed to reject request.');
    } finally {
      setActionLoading(false);
    }
  };

  const s = { // styles
    card: { background: 'rgba(16,10,32,0.72)', borderRadius: '0.75rem', border: '1px solid rgba(229,192,107,0.16)', boxShadow: '0 1px 3px rgba(0,0,0,0.3)' },
    label: { fontSize: '0.7rem', color: '#8D8471', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' },
    value: { fontSize: '0.875rem', color: '#E5DCC4', fontWeight: 500 },
    section: { marginBottom: '1.25rem' },
    sectionTitle: { fontSize: '0.7rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.15em', color: '#AEA08A', marginBottom: '0.75rem', paddingBottom: '0.4rem', borderBottom: '1px solid rgba(229,192,107,0.12)' },
  };

  return (
    <div style={{ padding: '2rem', maxWidth: '1200px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#F4EBD8', marginBottom: '0.25rem' }}>
          Centre Requests
        </h1>
        <p style={{ color: '#AEA08A', fontSize: '0.85rem' }}>
          Review and verify centre trust applications. Upon approval, the system provisions a Centre Admin Portal.
        </p>
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ padding: '0.85rem 1rem', marginBottom: '1rem', borderRadius: '0.5rem', fontSize: '0.875rem', background: toast.type === 'success' ? 'rgba(34,197,94,0.1)' : 'rgba(239,68,68,0.1)', color: toast.type === 'success' ? '#4ade80' : '#f87171', border: `1px solid ${toast.type === 'success' ? 'rgba(34,197,94,0.35)' : 'rgba(239,68,68,0.35)'}` }}>
          {toast.text}
        </div>
      )}

      {/* Approval Result: Show temp password */}
      {approveResult && (
        <div style={{ padding: '1.25rem', marginBottom: '1.25rem', borderRadius: '0.75rem', background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.3)' }}>
          <h3 style={{ color: '#4ade80', fontWeight: 700, marginBottom: '0.75rem' }}>✅ Centre Portal Created Successfully!</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div>
              <div style={s.label}>Login Email</div>
              <div style={{ ...s.value, fontFamily: 'monospace', background: '#e8fdf0', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', wordBreak: 'break-all' }}>{approveResult.loginEmail}</div>
            </div>
            <div>
              <div style={s.label}>Temporary Password <span style={{ color: '#e74c3c' }}>(share securely!)</span></div>
              <div style={{ ...s.value, fontFamily: 'monospace', background: '#fef9e7', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', fontWeight: 800, fontSize: '1rem', letterSpacing: '0.1em' }}>{approveResult.tempPassword}</div>
            </div>
            <div>
              <div style={s.label}>Stream Key</div>
              <div style={{ ...s.value, fontFamily: 'monospace', background: 'rgba(59,130,246,0.12)', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', fontSize: '0.78rem', wordBreak: 'break-all', color: '#93C5FD' }}>{approveResult.streamKey}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={s.label}>RTMP Ingest URL</div>
              <div style={{ ...s.value, fontFamily: 'monospace', background: 'rgba(59,130,246,0.10)', padding: '0.4rem 0.6rem', borderRadius: '0.4rem', fontSize: '0.75rem', wordBreak: 'break-all' }}>{approveResult.rtmpUrl}</div>
            </div>
          </div>
          <p style={{ color: '#C9BDA6', fontSize: '0.78rem', marginTop: '0.75rem', marginBottom: 0 }}>
            ⚠️ Share these credentials securely with the centre trust. They must change the password on first login.
          </p>
          <button onClick={() => setApproveResult(null)} style={{ marginTop: '0.75rem', padding: '0.4rem 1rem', background: '#15803d', color: '#F4EBD8', border: 'none', borderRadius: '0.4rem', cursor: 'pointer', fontSize: '0.8rem' }}>
            Dismiss
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: '1.5rem' }}>
        {/* Left: Request List */}
        <div style={{ flex: selected ? '0 0 420px' : '1' }}>
          {/* Filter Tabs */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            {['pending', 'approved', 'rejected'].map(st => (
              <button key={st} onClick={() => { setStatusFilter(st); setSelected(null); }}
                style={{ padding: '0.4rem 1rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.78rem', fontWeight: 700, textTransform: 'capitalize', background: statusFilter === st ? '#0E0E26' : '#f5f5f5', color: statusFilter === st ? '#E8B84B' : '#888', transition: 'all 0.2s' }}>
                {st}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: '#8D8471' }}>Loading...</div>
          ) : requests.length === 0 ? (
            <div style={{ ...s.card, padding: '3rem', textAlign: 'center', color: '#8D8471' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🪷</div>
              <p style={{ margin: 0 }}>No {statusFilter} centre requests.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {requests.map(req => {
                const sc = STATUS_COLORS[req.status] || STATUS_COLORS.pending;
                const isActive = selected?._id === req._id;
                return (
                  <div key={req._id} onClick={() => setSelected(req)}
                    style={{ ...s.card, padding: '1rem 1.25rem', cursor: 'pointer', borderColor: isActive ? '#E8B84B' : 'rgba(229,192,107,0.16)', background: isActive ? 'rgba(232,184,75,0.12)' : 'rgba(16,10,32,0.72)', transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                      <div>
                        <p style={{ fontWeight: 700, color: '#F4EBD8', margin: 0, fontSize: '0.925rem' }}>{req.centreName}</p>
                        <p style={{ color: '#AEA08A', fontSize: '0.78rem', margin: '0.15rem 0 0' }}>{req.primaryDeity} · {req.trustName}</p>
                      </div>
                      <span style={{ fontSize: '0.7rem', fontWeight: 700, padding: '0.2rem 0.65rem', borderRadius: '99px', background: sc.bg, border: `1px solid ${sc.border}`, color: sc.text, whiteSpace: 'nowrap' }}>
                        {req.status}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <p style={{ color: '#8D8471', fontSize: '0.72rem', margin: 0 }}>{req.trusteeEmail}</p>
                      <p style={{ color: '#8D8471', fontSize: '0.68rem', margin: 0 }}>{new Date(req.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Right: Detail Panel */}
        {selected && (
          <div style={{ flex: 1, ...s.card, padding: '1.5rem', overflowY: 'auto', maxHeight: '80vh', position: 'sticky', top: '2rem' }}>
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
              <div>
                <h2 style={{ fontWeight: 800, fontSize: '1.1rem', color: '#F4EBD8', marginBottom: '0.2rem' }}>{selected.centreName}</h2>
                <p style={{ color: '#AEA08A', fontSize: '0.8rem', margin: 0 }}>Applied {new Date(selected.createdAt).toLocaleString('en-IN')}</p>
              </div>
              <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8D8471', fontSize: '1.25rem' }}>✕</button>
            </div>

            {/* Centre Identity */}
            <div style={s.section}>
              <div style={s.sectionTitle}>🕌 Centre Identity</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div><div style={s.label}>Primary Deity</div><div style={s.value}>{selected.primaryDeity}</div></div>
                <div><div style={s.label}>Location</div><div style={s.value}>{selected.locationAddress}</div></div>
                {selected.locationLat && <div><div style={s.label}>Coordinates</div><div style={{ ...s.value, fontFamily: 'monospace' }}>{selected.locationLat}, {selected.locationLng}</div></div>}
              </div>
              {selected.description && <div style={{ marginTop: '0.75rem' }}><div style={s.label}>Description</div><div style={{ ...s.value, color: '#555', lineHeight: 1.5 }}>{selected.description}</div></div>}
            </div>

            {/* Trust Details */}
            <div style={s.section}>
              <div style={s.sectionTitle}>🏛️ Trust & Authority</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div><div style={s.label}>Trust Name</div><div style={s.value}>{selected.trustName}</div></div>
                <div><div style={s.label}>Head Trustee</div><div style={s.value}>{selected.headTrusteeName}</div></div>
                <div><div style={s.label}>Phone</div><div style={s.value}>{selected.trusteePhone}</div></div>
                <div><div style={s.label}>Official Email</div><div style={{ ...s.value, color: '#E8B84B' }}>{selected.trusteeEmail}</div></div>
              </div>
            </div>

            {/* Verification Documents */}
            <div style={s.section}>
              <div style={s.sectionTitle}>📋 Verification Documents</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div>
                  <div style={s.label}>Trust Reg. Certificate No.</div>
                  <div style={{ ...s.value, fontFamily: 'monospace', background: '#f9f7f3', padding: '0.3rem 0.5rem', borderRadius: '0.3rem' }}>{selected.trustRegCertNumber || '—'}</div>
                </div>
                <div>
                  <div style={s.label}>80G Certificate No.</div>
                  <div style={{ ...s.value, fontFamily: 'monospace', background: '#f9f7f3', padding: '0.3rem 0.5rem', borderRadius: '0.3rem' }}>{selected.cert80GNumber || 'Not applicable'}</div>
                </div>
              </div>
            </div>

            {/* Operations */}
            <div style={s.section}>
              <div style={s.sectionTitle}>⚙️ Operational Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div><div style={s.label}>Operating Hours</div><div style={s.value}>{selected.operatingHours || '—'}</div></div>
                <div><div style={s.label}>Camera Infrastructure</div><div style={s.value}>{selected.hasCameraInfra === 'yes' ? '✅ Available' : selected.hasCameraInfra === 'partial' ? '⚠️ Partial' : '❌ Needs Setup'}</div></div>
                <div><div style={s.label}>Head Priest</div><div style={s.value}>{selected.priestName || '—'}</div></div>
                <div><div style={s.label}>Priest Phone</div><div style={s.value}>{selected.priestPhone || '—'}</div></div>
              </div>
            </div>

            {/* Services */}
            {selected.servicesWanted?.length > 0 && (
              <div style={s.section}>
                <div style={s.sectionTitle}>🎯 Services to Digitize</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                  {selected.servicesWanted.map(sv => (
                    <span key={sv} style={{ padding: '0.3rem 0.7rem', background: 'rgba(212,175,55,0.1)', border: '1px solid rgba(212,175,55,0.25)', borderRadius: '99px', fontSize: '0.75rem', color: '#B8923A' }}>
                      {SERVICES_LABELS[sv] || sv}
                    </span>
                  ))}
                </div>
                {selected.servicesNotes && <p style={{ color: '#777', fontSize: '0.8rem', marginTop: '0.5rem' }}>{selected.servicesNotes}</p>}
              </div>
            )}

            {/* Bank Account */}
            <div style={s.section}>
              <div style={s.sectionTitle}>🏦 Bank Account</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                <div><div style={s.label}>Bank Name</div><div style={s.value}>{selected.bankName || '—'}</div></div>
                <div><div style={s.label}>Account Holder</div><div style={s.value}>{selected.bankAccountHolder || '—'}</div></div>
                <div><div style={s.label}>Account Number</div><div style={{ ...s.value, fontFamily: 'monospace', letterSpacing: '0.05em' }}>{selected.bankAccountNumber ? `****${selected.bankAccountNumber.slice(-4)}` : '—'}</div></div>
                <div><div style={s.label}>IFSC Code</div><div style={{ ...s.value, fontFamily: 'monospace' }}>{selected.bankIfscCode || '—'}</div></div>
              </div>
            </div>

            {/* Admin Note (if any) */}
            {selected.adminNote && (
              <div style={{ padding: '0.75rem', background: '#fafafa', border: '1px solid #eee', borderRadius: '0.5rem', marginBottom: '1rem' }}>
                <div style={s.label}>Admin Note</div>
                <p style={{ ...s.value, margin: 0 }}>{selected.adminNote}</p>
              </div>
            )}

            {/* Action Buttons (only for pending) */}
            {selected.status === 'pending' && (
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                <button
                  onClick={() => handleApprove(selected._id)}
                  disabled={actionLoading}
                  style={{ flex: 1, padding: '0.85rem', background: 'linear-gradient(90deg, #15803d, #16a34a)', border: 'none', borderRadius: '0.5rem', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '0.875rem', opacity: actionLoading ? 0.7 : 1 }}>
                  {actionLoading ? 'Processing...' : '✅ Approve & Provision Portal'}
                </button>
                <button
                  onClick={() => setShowRejectModal(selected._id)}
                  disabled={actionLoading}
                  style={{ flex: 1, padding: '0.85rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', color: '#b91c1c', fontWeight: 700, cursor: 'pointer', fontSize: '0.875rem' }}>
                  ✕ Reject
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#150E2E', borderRadius: '0.75rem', padding: '1.5rem', width: '100%', maxWidth: '440px', border: '1px solid rgba(229,192,107,0.2)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
            <h3 style={{ fontWeight: 700, marginBottom: '0.75rem', color: '#F4EBD8' }}>Reject Centre Request</h3>
            <p style={{ color: '#AEA08A', fontSize: '0.85rem', marginBottom: '1rem' }}>Provide a reason. This will be recorded for audit.</p>
            <textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              rows="3"
              placeholder="e.g. Trust registration certificate is expired. Please renew and reapply."
              style={{ width: '100%', padding: '0.75rem', border: '1px solid #ddd', borderRadius: '0.5rem', fontSize: '0.875rem', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={() => { setShowRejectModal(null); setRejectNote(''); }} style={{ flex: 1, padding: '0.75rem', background: '#f5f5f5', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: 600, color: '#C9BDA6' }}>
                Cancel
              </button>
              <button onClick={() => handleReject(showRejectModal)} disabled={actionLoading} style={{ flex: 1, padding: '0.75rem', background: '#b91c1c', border: 'none', borderRadius: '0.5rem', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CentreRequests;
